export enum OstiumErrorCode {
  INVALID_CONFIG = 'INVALID_CONFIG',
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  ALLOWANCE_INSUFFICIENT = 'ALLOWANCE_INSUFFICIENT',
  SUBMISSION_FAILED = 'SUBMISSION_FAILED',
  DELEGATION_FAILED = 'DELEGATION_FAILED',
  CONTRACT_ERROR = 'CONTRACT_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  /**
   * The operation was submitted but its outcome is unknown — do NOT resubmit.
   * See {@link OstiumSubmissionPendingError}.
   */
  SUBMISSION_PENDING = 'SUBMISSION_PENDING',
}

export class OstiumError extends Error {
  constructor(
    message: string,
    public readonly code: OstiumErrorCode,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'OstiumError';
  }
}

/**
 * Submitted, outcome unknown.
 *
 * A UserOperation that has been accepted by the bundler is not cancelled when
 * we stop waiting for its receipt — it can still land. Reporting that as a
 * plain failure invites the caller to retry and open the position twice, so it
 * gets its own type carrying the hash needed to reconcile.
 *
 * ```ts
 * catch (e) {
 *   if (e instanceof OstiumSubmissionPendingError) {
 *     // Poll the bundler for e.userOpHash, or reconcile against
 *     // getOpenPositions() / getOrders(). Never resubmit blindly.
 *   }
 * }
 * ```
 */
export class OstiumSubmissionPendingError extends OstiumError {
  constructor(
    message: string,
    /** Hash of the in-flight UserOperation. */
    public readonly userOpHash: `0x${string}`,
    cause?: unknown,
  ) {
    super(message, OstiumErrorCode.SUBMISSION_PENDING, cause);
    this.name = 'OstiumSubmissionPendingError';
  }
}
